import { FC, ReactElement, memo, useCallback } from 'react';
import { useIsMutating } from 'react-query';

import { AppMessage } from '@/common/components';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import {
  DomainRegistrarAccountsQueryKeys,
  useDeleteDomainRegistrarAccountsMutation,
  useGetAllDomainRegistrarAccountsQuery,
} from '@/modules/DomainRegistrars';

import { DomainRegistrarAccountsListView } from './DomainRegistrarAccountsListView';

interface Props {
  domainRegistrarId: string;
}

export const DomainRegistrarAccountsListContainer: FC<Props> = memo(({ domainRegistrarId }) => {
  const appMessageApi = AppMessage.useControls();

  const {
    data: domainRegistrarAccountsData,
    error: domainRegistrarAccountsError,
    isLoading: isDomainRegistrarAccountsListLoading,
    isFetching: isDomainRegistrarAccountsListFetching,
    isError: isDomainRegistrarAccountsError,
    refetch: refetchDomainRegistrarAccounts,
  } = useGetAllDomainRegistrarAccountsQuery(domainRegistrarId)({
    enabled: !!domainRegistrarId,
  });

  const { mutate: deleteDomainRegistrarAccountMutation, isLoading: isDeleteDomainRegistrarAccountLoading } =
    useDeleteDomainRegistrarAccountsMutation(domainRegistrarId)({
      onSuccess: () => {
        refetchDomainRegistrarAccounts();
      },
      onError: (err) => {
        messageOnError(err, appMessageApi);
      },
    });

  const isCreateAccountFetching = !!useIsMutating([DomainRegistrarAccountsQueryKeys.Create, domainRegistrarId], {
    exact: true,
  });

  const handleDeleteAccount = useCallback((accountId: string): void => {
    deleteDomainRegistrarAccountMutation(accountId);
  }, []);

  return (
    <FallbackedLayout
      data={domainRegistrarAccountsData}
      isLoading={isDomainRegistrarAccountsListLoading}
      isError={isDomainRegistrarAccountsError}
      error={domainRegistrarAccountsError}
    >
      {(domainRegistrarAccounts): ReactElement => (
        <DomainRegistrarAccountsListView
          domainRegistrarAccountsDataSource={domainRegistrarAccounts}
          disableControlButtons={
            isCreateAccountFetching || isDeleteDomainRegistrarAccountLoading || isDomainRegistrarAccountsListFetching
          }
          handleDeleteAccount={handleDeleteAccount}
        />
      )}
    </FallbackedLayout>
  );
});
